// WorkList — the one searchable / status-filtered / sorted / paged list of
// work items behind every Work tab (My Work, Created by Me, Collaborating,
// Team Work). The tab passes its server filter (assignee=me, created_by=me,
// collaborating=me, scope=team); the API narrows — this component never
// filters people or items client-side. Sub-items nest under their parent when
// both are in the page so they never read as duplicate rows.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { ListWorkbench, LoadMore, sumStatusCounts, totalStatusCount, useDebouncedValue } from '@/features/shared/components/ListWorkbench';
import { getApiErrorMessage } from '@/lib/apiError';
import { SubitemRollup, WORK_REMOVAL_REASONS, WorkItem, WorkListParams, listWorkItems, workItemCountsWithRemoved } from '@/services/workService';
import { useHasPermission } from '@/rbac/useRbac';
import { usePeopleNames } from '@/features/work/api/hooks';
import { RoleChip, WorkStatusBadge, WORK_STATUS_FILTERS } from './shared';
import { HealthChip } from './workload/health';
import { WorkRowActions } from './WorkRowActions';
import { fmtDT } from '../workassignments/shared';

export const OPEN_STATUSES = ['assigned', 'started', 'in_progress', 'changes_requested', 'on_hold', 'submitted_for_completion', 'under_review'];
export const OPEN_KEY = OPEN_STATUSES.join(',');
/** The Removed chip (work:remove only): not a status — it switches the list to
 *  the company's removed items (?removed=only). */
export const REMOVED_KEY = 'removed';
const PAGE = 20;

export type WorkListFilter = Pick<WorkListParams, 'assignee' | 'created_by' | 'collaborating' | 'scope' | 'queue' | 'project_id'>;

export interface WorkListProps {
  /** Server-side population filter for this tab. */
  filter: WorkListFilter;
  /** Initial status chip ('' = All). My Work defaults to Open. */
  defaultStatus?: string;
  /** Bump to refetch the first page (after create / mutations). */
  refreshKey?: number;
  /** Show the owner's name on each row (tabs where the owner isn't you). */
  showOwner?: boolean;
  emptyLabel: string;
  noMatchLabel: string;
  onOpen: (id: string) => void;
}

export const WorkList: React.FC<WorkListProps> = ({ filter, defaultStatus = '', refreshKey = 0, showOwner, emptyLabel, noMatchLabel, onOpen }) => {
  const { t } = useTranslation('work');
  const [list, setList] = useState<WorkItem[]>([]);
  const [total, setTotal] = useState(0);
  const [subCounts, setSubCounts] = useState<Record<string, SubitemRollup>>({});
  const [q, setQ] = useState('');
  const qDebounced = useDebouncedValue(q, 300);
  const [status, setStatus] = useState(defaultStatus);
  const [sort, setSort] = useState<'updated' | 'planned'>('updated');
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  // Bumped after a correction from a row's menu (reassign / cancel / stop
  // series) so the list and its chip counts refetch.
  const [localRefresh, setLocalRefresh] = useState(0);
  const refresh = () => setLocalRefresh((n) => n + 1);
  const filterKey = JSON.stringify(filter);
  const canSeeRemoved = useHasPermission('work:remove');
  const [removedCount, setRemovedCount] = useState<number | null>(null);

  const baseParams = useMemo<WorkListParams>(
    () => (status === REMOVED_KEY
      ? { limit: PAGE, sort, removed: 'only' as const, ...(qDebounced ? { q: qDebounced } : {}) }
      : { ...filter, limit: PAGE, sort, ...(status ? { status } : {}), ...(qDebounced ? { q: qDebounced } : {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey, sort, status, qDebounced],
  );

  useEffect(() => {
    let cancelled = false;
    listWorkItems({ ...baseParams, offset: 0 })
      .then((p) => { if (cancelled) return; setList(p.items); setTotal(p.total); setSubCounts(p.subitem_counts || {}); })
      .catch((e) => { if (!cancelled) toast.error(getApiErrorMessage(e, t('list_load_failed', { defaultValue: 'Could not load work items.' }))); });
    return () => { cancelled = true; };
  }, [baseParams, refreshKey, localRefresh, t]);

  // Status-chip counts depend on the population + search, not the active status.
  useEffect(() => {
    let cancelled = false;
    workItemCountsWithRemoved({ ...filter, ...(qDebounced ? { q: qDebounced } : {}) })
      .then((c) => { if (!cancelled) { setCounts(c.counts); setRemovedCount(c.removed); } })
      .catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, qDebounced, refreshKey, localRefresh]);

  const loadMore = () => {
    setLoadingMore(true);
    listWorkItems({ ...baseParams, offset: list.length })
      .then((p) => { setList((prev) => [...prev, ...p.items]); setTotal(p.total); setSubCounts((c) => ({ ...c, ...(p.subitem_counts || {}) })); })
      .catch((e) => toast.error(getApiErrorMessage(e, t('list_load_failed', { defaultValue: 'Could not load work items.' }))))
      .finally(() => setLoadingMore(false));
  };

  // Owner names: the list payload carries `owner_name` (server-resolved); only
  // rows without it fall back to the per-id /names lookup.
  const { nameOf } = usePeopleNames(showOwner ? list.filter((w) => !w.owner_name).map((w) => w.owner_employee_id) : []);
  const ownerNameOf = (w: WorkItem) => w.owner_name || nameOf(w.owner_employee_id);
  // Collaborating tab: every row is work I contribute to but do not own —
  // say so on the row (role chip + "Owner: X") so it never reads as mine.
  const collaboratingTab = filter.collaborating === 'me';

  // Nest sub-items under their parent when the parent is in the list too.
  const inList = useMemo(() => new Set(list.map((w) => w.id)), [list]);
  const childrenOf = useMemo(() => {
    const m: Record<string, WorkItem[]> = {};
    list.forEach((w) => { if (w.parent_id && inList.has(w.parent_id)) (m[w.parent_id] ||= []).push(w); });
    return m;
  }, [list, inList]);
  const top = useMemo(() => list.filter((w) => !(w.parent_id && inList.has(w.parent_id))), [list, inList]);

  // A render FUNCTION, not a component declared inside render: a component
  // defined here is a new type every render, so React would remount each row
  // (and close a row's open Reassign / Cancel dialog) whenever the list
  // re-rendered — e.g. when an owner's name resolved.
  const renderRow = (w: WorkItem, nested = false): React.ReactNode => {
    const sub = subCounts[w.id];
    const strayChild = !nested && !!w.parent_id;
    return (
      <React.Fragment key={w.id}>
        <div className={`flex w-full items-center gap-1 border-b border-border hover:bg-muted/50 ${nested ? 'pl-10 pr-2' : 'pl-4 pr-2'}`}>
          <button type="button" onClick={() => onOpen(w.id)}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 py-3 text-left">
            <div className="min-w-0">
              <p className="text-foreground truncate">
                {nested && <span className="text-muted-foreground mr-1">↳</span>}
                {w.title}
                {strayChild && <span className="ml-2 text-xs text-muted-foreground">{t('subitem_tag', { defaultValue: 'sub-item' })}</span>}
                {w.removed_at && <span className="ml-2 text-xs text-danger-strong">{t('removed_tag', { defaultValue: 'removed' })}</span>}
                {collaboratingTab && <RoleChip role="collaborator" className="ml-2 align-middle" />}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {showOwner && w.owner_employee_id
                  ? `${collaboratingTab
                    ? t('owner_label', { defaultValue: 'Owner: {{name}}', name: ownerNameOf(w) })
                    : ownerNameOf(w)} · `
                  : ''}
                {w.status === 'completed' && w.completed_at
                  ? `${t('completed_at', { defaultValue: 'Completed' })} ${fmtDT(w.completed_at)}`
                  : w.submitted_at
                    ? `${t('submitted_at', { defaultValue: 'submitted' })} ${fmtDT(w.submitted_at)}`
                    : `${t('updated', { defaultValue: 'Updated' })} ${fmtDT(w.updated_at)}`}
                {w.planned_start_at ? ` · ${t('planned', { defaultValue: 'Planned' })} ${fmtDT(w.planned_start_at)}` : ''}
                {sub?.total ? ` · ${sub.done}/${sub.total} ${t('sub_done', { defaultValue: 'sub-items done' })}` : ''}
              </p>
              {/* Removed view (W22): when, by whom and why — on the row, not only in the item. */}
              {w.removed_at && (
                <p className="text-xs text-danger-strong" data-testid={`removed-line-${w.id}`}>
                  {t('removed_line', {
                    defaultValue: 'Removed {{when}} by {{who}} · {{why}}',
                    when: fmtDT(w.removed_at),
                    who: w.removed_by_name || t('history_someone', { defaultValue: 'Someone' }),
                    why: t(`remove_reason_${w.removal_reason_code || 'other'}`, {
                      defaultValue: WORK_REMOVAL_REASONS.find((r) => r.code === w.removal_reason_code)?.label ?? 'Other',
                    }),
                  })}
                  {w.removal_note ? ` — ${w.removal_note}` : ''}
                </p>
              )}
            </div>
            <span className="flex items-center gap-2 shrink-0">
              {/* Work health (server-computed; "Why?" lists the reasons). On-track
                  stays quiet so the row only flags what needs a look. */}
              {w.health && w.health.primary_health !== 'ON_TRACK' && (
                <HealthChip health={w.health.primary_health} explain={w.health.explain} signals={w.health.signals} />
              )}
              <WorkStatusBadge status={w.status} />
            </span>
          </button>
          {/* Corrections live on the row — where people notice the mistake. */}
          <WorkRowActions item={w} ownerName={w.owner_employee_id ? ownerNameOf(w) : undefined} onOpen={onOpen} onChanged={refresh} />
        </div>
        {(childrenOf[w.id] || []).map((c) => renderRow(c, true))}
      </React.Fragment>
    );
  };

  const statuses = useMemo(
    () => [
      { key: OPEN_KEY, label: t('status_open', { defaultValue: 'Open' }) as string, count: sumStatusCounts(counts, OPEN_KEY) },
      ...WORK_STATUS_FILTERS.map((s) => ({ key: s.key, label: t(`status_${s.key.replace(/,/g, '_')}`, { defaultValue: s.label }) as string, count: sumStatusCounts(counts, s.key) })),
      // Administrators only — and never part of "All".
      ...(canSeeRemoved ? [{ key: REMOVED_KEY, label: t('status_removed', { defaultValue: 'Removed' }) as string, count: removedCount ?? 0 }] : []),
    ],
    [t, counts, canSeeRemoved, removedCount],
  );

  return (
    <div className="space-y-4">
      <ListWorkbench
        search={q}
        onSearch={setQ}
        searchPlaceholder={t('search_ph', { defaultValue: 'Search work items…' }) as string}
        statuses={statuses}
        activeStatus={status}
        onStatus={setStatus}
        allLabel={t('common.all', { defaultValue: 'All' }) as string}
        allCount={totalStatusCount(counts)}
        sorts={[
          { key: 'updated', label: t('sort_updated', { defaultValue: 'Recently updated' }) as string },
          { key: 'planned', label: t('sort_planned', { defaultValue: 'Planned date' }) as string },
        ]}
        activeSort={sort}
        onSort={(k) => setSort(k as 'updated' | 'planned')}
      />
      <Card><CardContent className="p-0">
        {top.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">{q || status !== defaultStatus ? noMatchLabel : emptyLabel}</p>
        ) : top.map((w) => renderRow(w))}
      </CardContent></Card>
      <LoadMore
        shown={list.length}
        total={total}
        loading={loadingMore}
        onMore={loadMore}
        moreLabel={t('common.load_more', { defaultValue: 'Load more' }) as string}
        countLabel={(s, n) => t('showing_of', { defaultValue: 'Showing {{count}} of {{total}}', count: s, total: n }) as string}
      />
    </div>
  );
};

export default WorkList;
